import type { ColumnDef } from '@tanstack/react-table';

export const COLUMN_MENU_ROOT = 'root';
export const COLUMN_MENU_OPERATOR = 'operator';

type MenuColumn = Pick<ColumnDef<any, any>, 'id' | 'meta'>;

export type ColumnMenuItem<C extends MenuColumn> =
  | { type: 'column'; id: string; column: C }
  | { type: 'group'; id: string; name: string; columns: C[] };

export const columnMenuGroupID = (name: string) => `group:${name}`;

function compareByOrder(order: string[]) {
  const position = (id: string | undefined) => {
    const index = id ? order.indexOf(id) : -1;
    return index === -1 ? order.length : index;
  };
  const tier = (col: MenuColumn) => {
    if (col.meta?.fixedPosition === 'start') return 0;
    if (col.meta?.fixedPosition === 'end') return 2;
    return 1;
  };

  return (a: MenuColumn, b: MenuColumn) => tier(a) - tier(b) || position(a.id) - position(b.id);
}

/**
 * Lays out the columns menu in table order: ungrouped columns and whole `meta.group` blocks share
 * one list so either can move past the other, while internal columns stay in their own section.
 */
export function buildColumnMenu<C extends MenuColumn>(
  columns: C[],
  order: string[],
  isOperatorColumn: (column: C) => boolean,
) {
  const compare = compareByOrder(order);
  const groups = new Map<string, C[]>();
  const ungrouped: C[] = [];
  const operator: C[] = [];

  for (const col of columns) {
    const group = col.meta?.group;
    if (group) {
      groups.set(group, [...(groups.get(group) ?? []), col]);
    } else if (isOperatorColumn(col)) {
      operator.push(col);
    } else {
      ungrouped.push(col);
    }
  }

  const items: (ColumnMenuItem<C> & { lead: C })[] = [
    ...ungrouped.map((column) => ({
      type: 'column' as const,
      id: column.id ?? '',
      column,
      lead: column,
    })),
    ...Array.from(groups, ([name, groupColumns]) => {
      const sorted = groupColumns.sort(compare);
      return {
        type: 'group' as const,
        id: columnMenuGroupID(name),
        name,
        columns: sorted,
        lead: sorted[0],
      };
    }),
  ].sort((a, b) => compare(a.lead, b.lead));

  return {
    items: items.map(({ lead, ...item }): ColumnMenuItem<C> => item),
    operatorColumns: operator.sort(compare),
  };
}

export function columnMenuItemColumnIDs(item: ColumnMenuItem<MenuColumn>): string[] {
  return item.type === 'column'
    ? [item.id]
    : item.columns.flatMap((col) => (col.id ? [col.id] : []));
}

/**
 * Moves `movingIDs` as one contiguous block to just past the `targetIDs` block, in the direction
 * of travel. Other columns keep their relative order.
 */
export function moveColumnIDs<K extends string>(
  order: K[],
  movingIDs: string[],
  targetIDs: string[],
  movingDown: boolean,
): K[] {
  const moving = order.filter((id) => movingIDs.includes(id));
  const rest = order.filter((id) => !movingIDs.includes(id));
  const targets = rest.filter((id) => targetIDs.includes(id));
  if (moving.length === 0 || targets.length === 0) return order;

  const at = movingDown ? rest.indexOf(targets[targets.length - 1]) + 1 : rest.indexOf(targets[0]);
  rest.splice(at, 0, ...moving);
  return rest;
}

/**
 * Picks the sibling a dragged item is over by its leading edge crossing that sibling's midpoint,
 * so items of any height (a whole group, a single column) swap at the same point.
 */
export function findSortableOver<ID>(
  activeIndex: number,
  dragged: { top: number; bottom: number },
  siblings: { id: ID; index: number; top: number; height: number }[],
): ID | undefined {
  const mid = (s: { top: number; height: number }) => s.top + s.height / 2;
  const above = siblings
    .filter((s) => s.index < activeIndex && dragged.top < mid(s))
    .sort((a, b) => a.index - b.index);
  if (above.length > 0) return above[0].id;

  const below = siblings
    .filter((s) => s.index > activeIndex && dragged.bottom > mid(s))
    .sort((a, b) => b.index - a.index);
  return below[0]?.id;
}
